import apiUrl from "../../../constants";
import axios from "axios";
import VueElementLoading from "vue-element-loading";
import { BasicSelect } from "vue-search-select";
import DatePicker from "vue2-datepicker";
import moment from "moment";

const AUTH_HEADERS = { Authorization: "authkey xB84JJ89Hd25" };
const ALL_LM_OPTION = { value: null, text: "All LM hubs" };
const MAX_DATE_SPAN_DAYS = 7;

export default {
  name: "CwbHoldBagsReport",
  components: { VueElementLoading, BasicSelect, DatePicker },

  data() {
    const today = moment().format("YYYY-MM-DD");
    const defaultFrom = moment().subtract(MAX_DATE_SPAN_DAYS, "days").format("YYYY-MM-DD");

    return {
      isLoading: false,
      isExporting: false,
      hasSearched: false,
      fmParentOptions: [],
      lmHubOptions: [ALL_LM_OPTION],
      selectedFmParent: { value: null, text: "" },
      selectedLmHub: ALL_LM_OPTION,
      fromDate: defaultFrom,
      toDate: today,
      maxToDate: today,
      minFromDate: "",
      reportData: null,
      reportToDate: null,
      dailySummary: [],
      expandedKeys: {},
      sealCache: {},
      expandLoadingKey: null,
    };
  },

  computed: {
    canSearch() {
      return (
        this.selectedFmParent.value &&
        this.fromDate &&
        this.toDate &&
        !this.isLoading
      );
    },
    canExport() {
      return (
        this.selectedFmParent.value &&
        this.fromDate &&
        this.toDate &&
        !this.isLoading &&
        !this.isExporting
      );
    },
    lmFilterLabel() {
      return this.selectedLmHub.value ? this.selectedLmHub.text : "all LM hubs";
    },
    largestBacklogLabel() {
      const hub = this.reportData && this.reportData.largestBacklogHub;
      if (!hub || !hub.hubName) return "—";
      return `${hub.hubName} (${this.formatNum(hub.bagHoldCount)} bags)`;
    },
    showKpiRow() {
      return this.hasSearched && this.reportData;
    },
  },

  mounted() {
    this.loadFmParents();
    this.syncDatePickerLimits();
  },

  methods: {
    apiBase() {
      return apiUrl.api_url_hubops;
    },

    async loadFmParents() {
      this.isLoading = true;
      try {
        const res = await axios.get(`${this.apiBase()}listFmParentHubsFromMapping`, {
          headers: AUTH_HEADERS,
        });
        if (res.data && res.data.ResultCode === 100) {
          this.fmParentOptions = (res.data.ResponseData || []).map((r) => ({
            value: r.fmParentHubId,
            text: r.fmParentHubName,
          }));
        } else {
          this.$alertify.error(
            (res.data && res.data.ReturnMessage) || "Failed to load FM parent hubs"
          );
        }
      } catch (err) {
        console.error(err);
        this.$alertify.error("Error loading FM parent hubs");
      } finally {
        this.isLoading = false;
      }
    },

    async onFmParentSelect(option) {
      this.selectedFmParent = option || { value: null, text: "" };
      this.selectedLmHub = ALL_LM_OPTION;
      this.lmHubOptions = [ALL_LM_OPTION];
      this.resetResults();

      if (!this.selectedFmParent.value) return;

      this.isLoading = true;
      try {
        const res = await axios.get(`${this.apiBase()}listLmHubsForFmParent`, {
          params: { fmParentHubId: this.selectedFmParent.value },
          headers: AUTH_HEADERS,
        });
        if (res.data && res.data.ResultCode === 100) {
          const opts = (res.data.ResponseData || []).map((r) => ({
            value: r.lmHubId,
            text: r.lmHubName,
          }));
          this.lmHubOptions = [ALL_LM_OPTION, ...opts];
        }
      } catch (err) {
        console.error(err);
        this.$alertify.error("Error loading LM hubs");
      } finally {
        this.isLoading = false;
      }
    },

    onLmHubSelect(option) {
      this.selectedLmHub = option || ALL_LM_OPTION;
    },

    syncDatePickerLimits() {
      const today = moment().format("YYYY-MM-DD");
      if (this.fromDate) {
        const maxTo = moment(this.fromDate).add(MAX_DATE_SPAN_DAYS, "days");
        this.maxToDate = maxTo.isAfter(today) ? today : maxTo.format("YYYY-MM-DD");
      } else {
        this.maxToDate = today;
      }
      if (this.toDate) {
        this.minFromDate = moment(this.toDate)
          .subtract(MAX_DATE_SPAN_DAYS, "days")
          .format("YYYY-MM-DD");
      } else {
        this.minFromDate = "";
      }
    },

    onFromDateChange() {
      this.syncDatePickerLimits();
      if (this.fromDate && this.toDate) {
        const from = moment(this.fromDate);
        const to = moment(this.toDate);
        if (to.isBefore(from, "day")) {
          this.toDate = from.format("YYYY-MM-DD");
        } else if (to.diff(from, "days") > MAX_DATE_SPAN_DAYS) {
          this.toDate = moment(this.fromDate)
            .add(MAX_DATE_SPAN_DAYS, "days")
            .format("YYYY-MM-DD");
        }
      }
      this.syncDatePickerLimits();
      this.resetResults();
    },

    onToDateChange() {
      this.syncDatePickerLimits();
      if (this.fromDate && this.toDate) {
        const from = moment(this.fromDate);
        const to = moment(this.toDate);
        if (from.isAfter(to, "day")) {
          this.fromDate = to.format("YYYY-MM-DD");
        } else if (to.diff(from, "days") > MAX_DATE_SPAN_DAYS) {
          this.fromDate = moment(this.toDate)
            .subtract(MAX_DATE_SPAN_DAYS, "days")
            .format("YYYY-MM-DD");
        }
      }
      this.syncDatePickerLimits();
      this.resetResults();
    },

    validateDates() {
      if (!this.fromDate || !this.toDate) {
        this.$alertify.error("From and To dates are required");
        return false;
      }
      const from = moment(this.fromDate, "YYYY-MM-DD");
      const to = moment(this.toDate, "YYYY-MM-DD");
      if (!from.isValid() || !to.isValid()) {
        this.$alertify.error("Invalid date format");
        return false;
      }
      if (to.isBefore(from)) {
        this.$alertify.error("To date must be on or after From date");
        return false;
      }
      if (to.diff(from, "days") > MAX_DATE_SPAN_DAYS) {
        this.$alertify.error("Date range cannot exceed 7 days");
        return false;
      }
      return true;
    },

    buildReportPayload() {
      const payload = {
        fmParentHubId: this.selectedFmParent.value,
        fromDate: moment(this.fromDate).format("YYYY-MM-DD"),
        toDate: moment(this.toDate).format("YYYY-MM-DD"),
      };
      if (this.selectedLmHub.value) {
        payload.lmDestinationHubId = this.selectedLmHub.value;
      }
      return payload;
    },

    async searchReport() {
      if (!this.validateDates()) return;

      this.isLoading = true;
      this.hasSearched = true;
      this.resetResults(false);
      const searchedToDate = moment(this.toDate).format("YYYY-MM-DD");

      try {
        const res = await axios.post(
          `${this.apiBase()}getHeldBagsByLmHubReport`,
          this.buildReportPayload(),
          { headers: AUTH_HEADERS }
        );

        if (res.data && res.data.ResultCode === 100) {
          this.reportToDate = searchedToDate;
          this.reportData = res.data.ResponseData;
          this.dailySummary = this.sortDailySummary(
            res.data.ResponseData.dailySummary || []
          );
        } else if (res.data && res.data.ResultCode === 101) {
          this.reportToDate = searchedToDate;
          this.reportData = {
            totalBagsOnHold: 0,
            totalShipmentsOnHold: 0,
            avgHoldAgeDays: 0,
            largestBacklogHub: null,
          };
          this.dailySummary = [];
        } else if (res.data && res.data.ResultCode === 104) {
          this.hasSearched = false;
          this.$alertify.error(
            (res.data && res.data.ReturnMessage) || "Invalid search filters"
          );
        } else {
          this.$alertify.error(
            (res.data && res.data.ReturnMessage) || "Search failed"
          );
        }
      } catch (err) {
        console.error(err);
        this.$alertify.error("Error fetching held bags report");
      } finally {
        this.isLoading = false;
      }
    },

    rowKey(row) {
      return `${row.holdDate}|${row.lmDestinationHubId}`;
    },

    isExpanded(row) {
      return !!this.expandedKeys[this.rowKey(row)];
    },

    async toggleExpand(row) {
      const key = this.rowKey(row);
      if (this.expandedKeys[key]) {
        this.$delete(this.expandedKeys, key);
        return;
      }
      this.$set(this.expandedKeys, key, true);

      if (this.sealCache[key]) return;

      this.expandLoadingKey = key;
      try {
        const res = await axios.post(
          `${this.apiBase()}getHeldBagSealsForDay`,
          {
            fmParentHubId: this.selectedFmParent.value,
            lmDestinationHubId: row.lmDestinationHubId,
            holdDate: this.normalizeHoldDate(row.holdDate),
          },
          { headers: AUTH_HEADERS }
        );
        if (res.data && res.data.ResultCode === 100) {
          this.$set(
            this.sealCache,
            key,
            (res.data.ResponseData && res.data.ResponseData.seals) || []
          );
        } else if (res.data && res.data.ResultCode === 101) {
          this.$set(this.sealCache, key, []);
        } else {
          this.$set(this.sealCache, key, []);
          this.$alertify.error(
            (res.data && res.data.ReturnMessage) || "Failed to load seals"
          );
        }
      } catch (err) {
        console.error(err);
        this.$set(this.sealCache, key, []);
        this.$alertify.error("Error loading seal detail");
      } finally {
        this.expandLoadingKey = null;
      }
    },

    async exportCsv() {
      if (!this.selectedFmParent.value) {
        this.$alertify.error("Select an FM parent hub to export");
        return;
      }
      if (!this.validateDates()) return;

      this.isExporting = true;
      try {
        const payload = this.buildReportPayload();

        const res = await axios.post(
          `${this.apiBase()}exportHeldBagsByLmHub`,
          payload,
          { headers: AUTH_HEADERS }
        );

        if (!res.data || res.data.ResultCode !== 100) {
          this.$alertify.error(
            (res.data && res.data.ReturnMessage) || "Export failed"
          );
          return;
        }

        const rows = (res.data.ResponseData && res.data.ResponseData.holdBagData) || [];
        if (!rows.length) {
          this.$alertify.error("No held bags to export");
          return;
        }

        const headers = [
          "bagSealNo",
          "lmDestinationHub",
          "lmDestinationHubId",
          "bagCreatedDate",
          "ageing",
          "shipmentCount",
        ];
        const csvLines = [
          headers.join(","),
          ...rows.map((r) =>
            [
              r.bagSealNo,
              r.lmDestinationHub,
              r.lmDestinationHubId,
              r.bagCreatedDate,
              r.ageing,
              r.shipmentCount,
            ]
              .map((cell) => `"${String(cell != null ? cell : "").replace(/"/g, '""')}"`)
              .join(",")
          ),
        ];
        const blob = new Blob([csvLines.join("\n")], {
          type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        const safeName = (this.selectedFmParent.text || "FM").replace(/[^\w\-/]/g, "_");
        link.href = url;
        link.setAttribute(
          "download",
          `HeldBags_${safeName}_${moment(this.fromDate).format("YYYYMMDD")}_${moment(this.toDate).format("YYYYMMDD")}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        this.$alertify.success(`Exported ${rows.length} bag(s)`);
      } catch (err) {
        console.error(err);
        this.$alertify.error("Error exporting held bags");
      } finally {
        this.isExporting = false;
      }
    },

    clearFilters() {
      this.selectedFmParent = { value: null, text: "" };
      this.selectedLmHub = ALL_LM_OPTION;
      this.lmHubOptions = [ALL_LM_OPTION];
      this.fromDate = moment().subtract(MAX_DATE_SPAN_DAYS, "days").format("YYYY-MM-DD");
      this.toDate = moment().format("YYYY-MM-DD");
      this.syncDatePickerLimits();
      this.resetResults();
      this.hasSearched = false;
    },

    resetResults(clearSearchFlag) {
      this.reportData = null;
      this.reportToDate = null;
      this.dailySummary = [];
      this.expandedKeys = {};
      this.sealCache = {};
      this.expandLoadingKey = null;
      if (clearSearchFlag !== false) {
        this.hasSearched = false;
      }
    },

    sortDailySummary(rows) {
      return rows.slice().sort((a, b) => {
        const d =
          moment(b.holdDate).valueOf() - moment(a.holdDate).valueOf();
        if (d !== 0) return d;
        return (a.lmDestinationHub || "").localeCompare(b.lmDestinationHub || "");
      });
    },

    normalizeHoldDate(d) {
      return moment(d).format("YYYY-MM-DD");
    },

    formatNum(n) {
      return Number(n || 0).toLocaleString("en-IN");
    },

    formatHoldDate(d) {
      return moment(this.normalizeHoldDate(d)).format("DD MMM YYYY");
    },
  },
};



// WEBPACK FOOTER //
// ./src/views/components/CwbHoldBagsReport/CwbHoldBagsReport.js